import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme, useMediaQuery, Button, CardMedia, Skeleton, Grid, CardActionArea, Card, CardContent, Typography, Box, IconButton } from "@mui/material";
import ArticleControls from "./ArticleControls.js";
import Tags from "../Tags.js";
import { ChevronLeft, ChevronRight, Visibility } from "@mui/icons-material";
import { Add } from "@mui/icons-material";
import { default as EventBus } from '../../utils/EventBus.js'
import CommunityEventNames from '../EventNames.js'
import { editArticleMetadata, createArticle } from '../utils.js'
import CircularProgress from '@mui/material/CircularProgress';
import DeleteArticleDialog from "./DeleteArticleDialog.js";

export default function SmallArticleList(props = {}) {
  const navigate = useNavigate()

  const [deleteArticleDialogOpen, setDeleteArticleDialogOpen] = useState(false)
  const [article, setArticle] = useState()

  const theme = useTheme();
  const isXl = useMediaQuery(theme.breakpoints.down('xl'));
  const isLg = useMediaQuery(theme.breakpoints.down('lg'));
  const isMd = useMediaQuery(theme.breakpoints.down('md'));
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));

  const deleteArticle = (article) => {
    setArticle(article)
    setDeleteArticleDialogOpen(true)
  }

  const addArticle = () => {
    EventBus.emit(CommunityEventNames.OPEN_ADD_EDIT_ARITCLE_DIALOG, {
      mode: 'add',
      article: {
        featured,
        news,
        ouCorner,
        highlighted,
        ouCornerCategory
      },
      visibility: {
        featured,
        news,
        ouCorner,
        highlighted,
        ouCornerCategory
      }
    })
  }

  const viewArticle = (article) => {
    if (article.newsWebUrl) {
      window.open(article.newsWebUrl, (article.newsWebUrl.includes(window.location.hostname) ? "_self" : "_blank"), "noopener,noreferrer");
    } else if(article.sitePath) {
      const {
        news,
        sitePath
      } = article
      
      const resourcePart = news ? 'news' : 'ou-corner'
      const path = `/${sitePath}/${resourcePart}/${article.guid}`
      navigate(path)
    } else {
      navigate(article.guid)
    }
  }

  const {
    articles,
    loading,
    loadingMore,
    editable,
    featured,
    news,
    ouCorner,
    highlighted,
    ouCornerCategory,
    published,
    showPrevious,
    showNext,
    onPrevious,
    onNext,
    tilesPerRow = 2,
    compact = false,
    author = true,
    tags = true,
    justifyTiles = 'flex-start',
    coverImage = false
  } = props

  const [hovering, setHovering] = useState()
  const verticalPaging = isSm || isMd

  const Wrapper = (props = {}) => {

    if (showNext) return <Grid container gap={0}>
      {/* Previous */}
      {showPrevious && <Grid xs={verticalPaging ? 12 : .5} className="mid-align-flex center-justify-flex ">
        <Button size='small' sx={{ minWidth: '100% !important', width: '100%', height: '100%' }} onClick={onPrevious}>
          {loadingMore && <CircularProgress />}
          {!loadingMore && <ChevronLeft sx={{ color: 'grey', transform: verticalPaging ? 'rotate(90deg)' : undefined }} fontSize="large" />}
        </Button>
      </Grid>}

      {/* Articles */}
      <Grid item xs={verticalPaging ? 12 : showPrevious ? 11 : 11.5}>
        {props.children}
      </Grid>

      {/* Next */}
      {showNext && <Grid xs={verticalPaging ? 12 : .5} className="mid-align-flex center-justify-flex">

        <Button size='small' sx={{ minWidth: '100% !important', width: '100%', height: '100%' }} onClick={onNext}>
          {loadingMore && <CircularProgress />}
          {!loadingMore && <ChevronRight sx={{ color: 'grey', transform: verticalPaging ? 'rotate(90deg)' : undefined }} fontSize="large" />}
        </Button>
      </Grid>}
    </Grid>

    return <>{props.children}</>
  }

  const lastRowCount = (articles?.length || 0) % tilesPerRow
  const lastRowStartsAt = (articles?.length || 0) - lastRowCount

  const spanFromTilesPerRow = useMemo(() => {
    const t = Number(tilesPerRow);
    if (!Number.isFinite(t) || t <= 0) return 12;
    if (justifyTiles === 'stretch') return 12 / tilesPerRow;

    const span = Math.round(12 / t);
    return Math.min(12, Math.max(1, span));
  }, [tilesPerRow, justifyTiles]);

  const spanForLastRow = useMemo(() => {
    if(justifyTiles !== 'stretch') return spanFromTilesPerRow
    const t = Number(tilesPerRow);
    if (!Number.isFinite(t) || t <= 0) return 12;

    return 12 / lastRowCount;
  }, [tilesPerRow, justifyTiles, lastRowCount, spanFromTilesPerRow]);

  const imageWidth = compact ? 105 : 170
  return <Wrapper>
    <DeleteArticleDialog
      open={deleteArticleDialogOpen}
      article={article}
      onClose={() => setDeleteArticleDialogOpen(false)}
    />
    <Grid container gap={0} className={!loading && editable ? 'article-section' : ''}
      sx={{
        display: 'flex',
        justifyContent: justifyTiles === 'stretch' ? 'center' : justifyTiles
      }}
    >
      {editable && !loading && <Grid item xs={12} className="add-icon">
        <div className="article-controls-backdrop">
          <IconButton onClick={addArticle} size='medium' className="icon-button">
            <Add />
          </IconButton>
        </div>
      </Grid>}
      {loading && <Grid container gap={0}>
        <Grid item xs={12} className="tertiary-article-height">
          <Skeleton className="fit-height"></Skeleton>
        </Grid>
      </Grid>}

      {!loading && !articles?.length && <span>No Articles</span>}
      {!loading && articles?.map((article, index) => {

        const span = justifyTiles === 'stretch' && index >= lastRowStartsAt
          ? spanForLastRow
          : spanFromTilesPerRow

        return <Grid key={`article_${index}`} item xs={12} sm={12} md={span} lg={span} xl={span} className="article-tile">
          <Card key={index} className={`tertiary-article-card tertiary-article-height${compact ? '-compact' : ''}`}>
            <CardActionArea
              component="div"
              className="tertiary-article-height"
              sx={{ display: 'flex', justifyContent: 'flex-start' }}
              onMouseOver={() => setHovering(index)}
              onMouseOut={() => setHovering()}
              onClick={() => viewArticle(article)}
            >
              {!coverImage && <CardMedia className='featured-image tertiary-article-height' component="div" sx={{ backgroundImage: `url(${article?.image})`, width: `${imageWidth}px !important`, float: 'left' }} alt="Featured Article" />}
              <CardMedia className={`featured-image-${coverImage ? 'fit' : 'blurred'} tertiary-article-height`} component="div" sx={{ backgroundImage: `url(${article?.image})`, width: `${imageWidth}px !important`, float: 'left' }} alt="Featured Article" />
              
              <CardContent className="tertiary-article-content" sx={
                { 
                  maxWidth: `calc(100% - ${imageWidth}px)`,
                  justifyContent: tags ? 'space-between !important' : 'center'
                }
                }>
                <Box className="tertiary-article-header text-clamp">
                  <Typography variant='body1' className="tertiary-article-title">
                    {article.title}
                  </Typography>
                </Box>

                {/* {author && <Typography noWrap={false} variant="caption" className="tertiary-article-meta">
                  {article.author} - {article.date}
                </Typography>} */}

                {tags && <Tags
                  tags={article?.tags || ""}
                  onSelectTag={_ => setHovering()}
                  clickable={published}
                  sx={{
                    overflow: 'hidden',
                    height: '30px',
                    alignItems: 'flex-end'
                  }}
                />}

              </CardContent>

              {editable && <ArticleControls
                hovering={hovering === index}
                isDraft={article.isDraft}
                isPinned={article?.metadata?.isPinned}
                layout='horizontal'
                onClickAdd={createArticle}
                onClickEdit={() => editArticleMetadata(article, {
                  featured,
                  news,
                  ouCorner,
                  highlighted,
                  ouCornerCategory,
                })}
                onClickDelete={() => deleteArticle(article)}
                article={article}
              />}
            </CardActionArea>

          </Card>
        </Grid>
      })}

    </Grid>
  </Wrapper>
}